import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AssessmentStatus } from "../../../../utils/enums";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  CardHeader,
  CircularProgress,
  useTheme,
  Checkbox,
  Tooltip,
  Alert,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import ReusableTable from "../../../../components/ReusableTable/ReusableTable";
import ConfirmSnackbar from "../../../../components/Snackbar/ConfirmSnackbar";
import AssessmentDropdown from "./components/AssessmentDropdown";
import AssessmentStatusCard from "./components/AssessmentStatusCard";
import BulkActionBar from "./components/BulkActionBar";
import ReviewEmptyState from "./components/ReviewEmptyState";
import ReviewHeaderActions from "./components/ReviewHeaderActions";
import { UserTableCell } from "./components/UserTableCell";
import { useUserReviewState } from "./hooks/useUserReviewState";
import { useReviewData } from "./hooks/useReviewData";
import { useAutoSave } from "./hooks/useAutoSave";
import { useAssessmentPermissions } from "./hooks/useAssessmentPermissions";
import { useAssessmentMutations } from "./hooks/useAssessmentMutations";
import { useAuth0 } from "@auth0/auth0-react";

const AccessSystemReview = ({ systemId: propSystemId, isEmbedded = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth0();
  const { systemId: paramSystemId, assessmentId } = useParams();

  const systemId = isEmbedded ? propSystemId : paramSystemId;
  const currentUserEmail = user?.email?.toLowerCase() || "";
  const [selectedAssessmentIdx, setSelectedAssessmentIdx] = useState(0);
  const [isSwitchingAssessment, setIsSwitchingAssessment] = useState(false);

  const {
    systemLoading,
    assessmentsLoading,
    loadingUsers,
    refetchSystems,
    refetchAssessments,
    refetchUsersData,
    allAssessments,
    currentSystem,
    users,
  } = useReviewData(systemId, assessmentId, isEmbedded);

  const currentAssessment = isEmbedded
    ? allAssessments[selectedAssessmentIdx]
    : allAssessments[0];

  const handleAssessmentChange = async (idx) => {
    setSelectedAssessmentIdx(idx);
    const selected = allAssessments[idx];
    if (!selected) return;

    try {
      setIsSwitchingAssessment(true);
      await refetchUsersData({
        page: 1,
        limit: 1000,
        filter: {
          resource_id: selected.resource_id ? [Number(selected.resource_id)] : [],
          assessment_id: selected.id,
        },
      });
    } finally {
      setIsSwitchingAssessment(false);
    }
  };

  useEffect(() => {
    if (currentAssessment?.resource_id) {
      refetchSystems();
      refetchUsersData();
    }
  }, [currentAssessment?.resource_id, refetchSystems, refetchUsersData]);

  const {
    userIsOwner,
    isCompletedOrRejected,
    isSystemArchived,
    isDraftAndNotOwner,
    shouldDisableActions,
  } = useAssessmentPermissions(
    currentSystem,
    currentUserEmail,
    currentAssessment
  );

  const {
    decisions,
    commentsByUser,
    selectedUsers,
    bulkDecision,
    bulkComment,
    allUsersReviewed,
    submitValidationMessage,
    handleDecisionChange,
    handleCommentChange,
    handleUserSelection,
    handleSelectAll,
    handleBulkDecisionChange,
    handleBulkCommentChange,
    handleBulkSave,
  } = useUserReviewState(users, currentAssessment);

  const {
    mutateUpdateAssessment,
    isSubmitting,
    isDiscarding,
    snackbarInfo,
    setSnackbarInfo,
    submitAssessment,
    discardAssessment,
  } = useAssessmentMutations(
    currentAssessment,
    users,
    decisions,
    commentsByUser,
    refetchAssessments,
    refetchUsersData
  );

  useAutoSave({
    decisions,
    commentsByUser,
    users,
    currentAssessment,
    mutateUpdateAssessment,
  });

  const columns = useMemo(() => {
    if (isCompletedOrRejected) {
      return [
        { label: "Name", field: "user_name", width: "15%" },
        { label: "Email", field: "user_email", width: "20%" },
        { label: "Role", field: "role", width: "20%" },
        {
          label: "Decision",
          field: "review_status",
          width: "15%",
          align: "center",
        },
        {
          label: "Comments",
          field: "review_notes",
          width: "25%",
          align: "center",
        },
      ];
    }
    return [
      { label: "", field: "checkbox", width: "5%", align: "center" },
      { label: "Name", field: "user_name", width: "20%" },
      { label: "Email", field: "user_email", width: "22%" },
      { label: "Role", field: "role", width: "10%" },
      { label: "Action", field: "actions", width: "30%", align: "center" },
    ];
  }, [isCompletedOrRejected]);

  const renderCell = (row, column) => {
    const isDisabled = isCompletedOrRejected || shouldDisableActions;

    return (
      <UserTableCell
        row={{ ...row, assessmentStatus: currentAssessment?.status_title }}
        column={column}
        decisions={decisions}
        commentsByUser={commentsByUser}
        selectedUsers={selectedUsers}
        isDisabled={isDisabled}
        isCompletedOrRejected={isCompletedOrRejected}
        isSystemArchived={isSystemArchived}
        isDraftAndNotOwner={isDraftAndNotOwner}
        onDecisionChange={handleDecisionChange}
        onCommentChange={handleCommentChange}
        onUserSelection={handleUserSelection}
      />
    );
  };

  const renderHeaderCell = (column) => {
    if (column.field === "checkbox") {
      if (isCompletedOrRejected) {
        return null;
      }
      const isDisabled = isCompletedOrRejected || shouldDisableActions;
      const tooltipMessage = isSystemArchived
        ? "System is archived. Activate it to start a review."
        : isDraftAndNotOwner
          ? "You are not an owner of the system"
          : "";

      return (
        <Tooltip title={tooltipMessage} arrow>
          <span>
            <Checkbox
              checked={
                selectedUsers.length === users.length && users.length > 0
              }
              indeterminate={
                selectedUsers.length > 0 && selectedUsers.length < users.length
              }
              onChange={handleSelectAll}
              disabled={isDisabled}
              sx={{
                p: 0,
                color: "#9CA3AF",
                "&.Mui-checked": {
                  color: "#3B82F6",
                },
                "&.MuiCheckbox-indeterminate": {
                  color: "#3B82F6",
                },
                "&.Mui-disabled": {
                  color: "#D1D5DB",
                },
              }}
            />
          </span>
        </Tooltip>
      );
    }
    return column.label;
  };

  if (systemLoading || assessmentsLoading || loadingUsers || isSwitchingAssessment) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (
    !systemLoading &&
    !assessmentsLoading &&
    !loadingUsers &&
    !currentSystem &&
    users.length === 0
  ) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h5" color="error">
          System not found
        </Typography>
        <Button
          onClick={() => navigate("/admin/access")}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Access Management
        </Button>
      </Box>
    );
  }

  const isDraft =
    currentAssessment?.status_title?.toLowerCase() === AssessmentStatus.DRAFT;

  return (
    <>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />

      {!isEmbedded && (
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/admin/access/system/${systemId}`)}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              color: theme.customColors?.text?.secondary || "#6B7280",
              "&:hover": {
                backgroundColor:
                  theme.customColors?.background?.hover || "#F3F4F6",
              },
            }}
          >
            Back to System Details
          </Button>
        </Box>
      )}

      {isEmbedded && allAssessments.length === 0 ? (
        <ReviewEmptyState
          title="No Access Reviews Yet"
          message="Start an access review from the User Management tab to see reviews here"
        />
      ) : !isEmbedded && !currentAssessment ? (
        <ReviewEmptyState
          title="Assessment not found"
          message="This assessment may have been deleted or does not exist"
        />
      ) : (
        <Card
          sx={{
            borderRadius: "12px",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            border: "1px solid #E8EAED",
          }}
          variant="outlined"
        >
          <CardHeader
            title={
              <Typography
                variant="h5"
                fontWeight="600"
                color={theme.customColors?.text?.strong}
              >
                Access Reviews
              </Typography>
            }
            sx={{
              borderBottom: "1px solid #ddd",
              background: "#F7F7FC",
              ".MuiCardHeader-title": {
                fontSize: "16px",
                fontWeight: "bold",
              },
              padding: "14px",
            }}
            action={
              <ReviewHeaderActions
                isDraft={isDraft}
                isSubmitting={isSubmitting}
                isDiscarding={isDiscarding}
                shouldDisableActions={shouldDisableActions}
                isSystemArchived={isSystemArchived}
                isDraftAndNotOwner={isDraftAndNotOwner}
                allUsersReviewed={allUsersReviewed}
                submitValidationMessage={submitValidationMessage}
                onSubmit={submitAssessment}
                onDiscard={discardAssessment}
              />
            }
          />
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={2.25}>
              {isEmbedded && allAssessments.length > 0 && (
                <AssessmentDropdown
                  assessments={allAssessments}
                  selectedAssessmentIdx={selectedAssessmentIdx}
                  onAssessmentChange={handleAssessmentChange}
                  currentSystem={currentSystem}
                />
              )}

              {currentAssessment && (
                <AssessmentStatusCard
                  currentAssessment={currentAssessment}
                  currentSystem={currentSystem}
                />
              )}

              {currentAssessment && (
                <>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: theme.customColors?.text?.strong || "#111827",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 2,
                    }}
                  >
                    Users ({users.length})
                    {isDraft && (
                      <Alert
                        severity="info"
                        sx={{ borderRadius: 1, px: 1.5, py: 0 }}
                      >
                        <Typography variant="body2">
                          All users are marked as &quot;Approved&quot; by
                          default. Use Modify or Revoke when needed.
                        </Typography>
                      </Alert>
                    )}
                  </Typography>

                  {selectedUsers.length >= 2 &&
                    isDraft &&
                    !isCompletedOrRejected &&
                    !shouldDisableActions && (
                      <Box sx={{ mb: 2 }}>
                        <BulkActionBar
                          selectedCount={selectedUsers.length}
                          bulkDecision={bulkDecision}
                          bulkComment={bulkComment}
                          onBulkDecisionChange={handleBulkDecisionChange}
                          onBulkCommentChange={handleBulkCommentChange}
                          onSave={handleBulkSave}
                          disabled={shouldDisableActions}
                        />
                      </Box>
                    )}

                  <Box sx={{ position: "relative" }}>
                    <ReusableTable
                      columns={columns}
                      data={users}
                      renderCell={renderCell}
                      renderHeaderCell={renderHeaderCell}
                      rowsPerPage={10}
                      sx={{
                        "& .MuiTableHead-root": {
                          bgcolor: "background.paper",
                        },
                        "& .MuiTableCell-head": {
                          fontWeight: 600,
                          color: "text.primary",
                          borderBottom: `2px solid ${theme.customColors?.border?.main || grey[300]
                            }`,
                        },
                        "& .MuiTableRow-root": {
                          "&:hover": {
                            bgcolor: "rgba(3, 27, 137, 0.04)",
                          },
                        },
                        "& .MuiTableCell-root": {
                          borderBottom: `1px solid ${theme.customColors?.border?.light || grey[200]
                            }`,
                        },
                      }}
                    />
                  </Box>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default AccessSystemReview;
